import { useCallback, useEffect, useRef } from "react";

// Event DOM yang dianggap "aktivitas pengguna". Semuanya bubble sampai window,
// jadi ini tetap terdeteksi walau terjadi di dalam form/dialog manapun.
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];

// mousemove/scroll bisa fire puluhan kali per detik — throttle supaya reset
// timer tidak dipanggil berlebihan.
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Reset dua timer (warning & logout) setiap ada aktivitas pengguna.
 * Mengembalikan `resetTimers` supaya caller bisa reset manual (mis. tombol "Tetap Masuk").
 *
 * Begitu dialog peringatan tampil (warningTimer jatuh tempo), aktivitas pasif
 * (mouse/scroll/keyboard) BERHENTI mereset logoutTimer -- sebelumnya gerakan
 * mouse sekecil apa pun saat dialog terbuka diam-diam membatalkan logout tanpa
 * menutup/mensinkronkan dialognya, jadi dialog terlihat "nyangkut" tapi sesi tidak
 * pernah benar-benar berakhir. Sekarang, sekali masuk fase warning, HANYA
 * `resetTimers()` yang dipanggil eksplisit (dari tombol "Tetap Masuk") yang boleh
 * membatalkan logout & mengaktifkan lagi reset-dari-aktivitas-biasa.
 */
export function useIdleTimeout({ warningMs, logoutMs, onWarning, onLogout, enabled = true }) {
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const lastActivityRef = useRef(0);
    const pausedRef = useRef(false);

    const clearTimers = useCallback(() => {
        clearTimeout(warningTimerRef.current);
        clearTimeout(logoutTimerRef.current);
    }, []);

    const resetTimers = useCallback(() => {
        clearTimers();
        pausedRef.current = false; // reset eksplisit -> aktivitas biasa aktif lagi sampai warning berikutnya
        if (!enabled) return;
        warningTimerRef.current = setTimeout(() => {
            // Mulai fase warning: matikan reset-dari-aktivitas-pasif SEBELUM
            // memanggil onWarning, supaya tidak ada celah balapan dgn event
            // aktivitas yg mungkin nyaris bersamaan.
            pausedRef.current = true;
            onWarning();
        }, warningMs);
        logoutTimerRef.current = setTimeout(onLogout, logoutMs);
    }, [enabled, warningMs, logoutMs, onWarning, onLogout, clearTimers]);

    useEffect(() => {
        if (!enabled) {
            clearTimers();
            return undefined;
        }

        const handleActivity = () => {
            if (pausedRef.current) return; // sedang fase warning -- tunggu klik eksplisit "Tetap Masuk"
            const now = Date.now();
            if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
            lastActivityRef.current = now;
            resetTimers();
        };

        resetTimers();
        ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

        return () => {
            clearTimers();
            ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
        };
    }, [enabled, resetTimers, clearTimers]);

    return { resetTimers };
}
